// ============================================================================
// CUSTOMER HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { customerService } from 'api/services/customerService';
import type { Customer, CreateCustomerRequest, UpdateCustomerRequest } from 'api/types/customer.types';
import type { ID } from 'api/types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'customers';

export const useCustomers = () => {
  const [data, setData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useDataChangeListener(DATA_KEY, fetchCustomers);

  return { data, loading, error, refetch: fetchCustomers };
};

export const useCreateCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCustomer = async (data: CreateCustomerRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.create(data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createCustomer, loading, error };
};

export const useUpdateCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateCustomer = async (id: ID, data: UpdateCustomerRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.update(id, data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateCustomer, loading, error };
};

export const useDeleteCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteCustomer = async (id: ID) => {
    setLoading(true);
    setError(null);
    try {
      await customerService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteCustomer, loading, error };
};
